import axios, { AxiosError } from 'axios';
import { API_URL_LOCAL, API_URL_PRODUCTION } from '../config';
import {
  CouponCodeValidateResponse,
  InsuranceCalculateRequest,
  InsuranceFormSubmission,
  InsuranceFormSubmissionResponse,
  InsuranceQuote,
  InsuranceType,
} from '../types/insurance.types';

const isLocalHost = window.location.hostname === 'localhost';
const baseApiUrl = isLocalHost ? API_URL_LOCAL : API_URL_PRODUCTION;

/**
 * API Error type for structured error handling
 */
export interface ApiError {
  statusCode: number;
  message: string;
  field?: string;
  error: string;
}

/**
 * Extract error message from Axios error response
 */
function extractErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiError>;
    // Try to get message from response data
    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }
    // Fallback to status text
    if (axiosError.response?.statusText) {
      return axiosError.response.statusText;
    }
  }
  // For regular errors
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unknown error occurred';
}

export async function getInsurancePlans(): Promise<InsuranceType[]> {
  try {
    const response = await axios.get(`${baseApiUrl}/list`);
    return response.data;
  } catch (error) {
    console.error('Error fetching insurance list:', error);
    throw new Error(extractErrorMessage(error));
  }
}

export async function validateInsuranceCalculateRequest(
  params: InsuranceCalculateRequest
): Promise<{ valid: boolean; error?: string }> {
  try {
    const verify = await axios.post(`${baseApiUrl}/validate`, params);

    if (verify.data.status === 'error') {
      return { valid: false, error: verify.data.message };
    }

    return { valid: true };
  } catch (error) {
    const errorMessage = extractErrorMessage(error);
    return { valid: false, error: errorMessage };
  }
}

export async function getInsuranceCalculate(
  params: InsuranceCalculateRequest
): Promise<InsuranceQuote> {
  try {
    // First validate the request
    const validation = await validateInsuranceCalculateRequest(params);

    if (!validation.valid) {
      throw new Error(validation.error || 'Invalid insurance calculate request');
    }

    const response = await axios.get(`${baseApiUrl}/calculate`, {
      params,
    });

    return response.data;
  } catch (error) {
    const errorMessage = extractErrorMessage(error);
    console.error('Error fetching insurance calculate:', errorMessage);
    throw new Error(errorMessage);
  }
}

export async function getSubmitInsurance(params: InsuranceFormSubmission): Promise<InsuranceFormSubmissionResponse> {
  try {
    const response = await axios.post(`${baseApiUrl}/submit`, params);
    return response.data;
  } catch (error) {
    console.error('Error submitting insurance:', error);
    throw error;
  }
}

export async function couponCodeValidate(couponCode: string): Promise<CouponCodeValidateResponse> {
  try {
    const response = await axios.get(`${baseApiUrl}/coupon`, { params: { couponCode } });
    return response.data.valid === 'valid' ?
      { valid: true } :
      { valid: false };
  } catch (error) {
    console.error('Error validating coupon code:', error);
    throw error;
  }
}
